import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '../../components/Button/Button';
import { CarouselArrow } from '../../components/CarouselArrow/CarouselArrow';
import { useInView } from '../../hooks/useInView';
import { AthleteCard } from './AthleteCard';
import { MobileAthleteFeature } from './MobileAthleteFeature';
import { MobileAthleteDrawer } from './MobileAthleteDrawer';
import type { Athlete } from './athletes';

import './AthleteCarousel.css';

// Three copies of the athlete list let the open card slide off viewport-left
// while a twin from a later copy fills its spot. After the slide, trackIdx
// is snapped back into the middle copy with transitions disabled (the wrap
// is invisible because every copy renders the same athlete at the same
// viewport position).
const COPIES = 3;
const COLLAPSED_W = 135;
const GAP = 8;
// Horizontal padding on .athlete-carousel__viewport (8px each side).
const VIEWPORT_PADDING_X = 16;
// Floor for the expanded card so a long roster doesn't squash it down toward
// the collapsed width. Matches the .athlete-card--expanded CSS design width.
// Once it kicks in, the extra collapsed peeks overflow the viewport (clipped).
// Capped at the viewport width so it never overflows.
const EXPANDED_MIN_W = 947;
const REVEAL_OPTIONS: IntersectionObserverInit = { threshold: 0.15 };
// Matches the 1024px CSS breakpoint where the layout swaps to the mobile stage.
const DESKTOP_QUERY = '(min-width: 640px)';

type AthleteCarouselProps = {
    athletes: Athlete[];
};

// True only on the desktop layout. The desktop track mounts n * COPIES <video>
// elements; on mobile that pile exhausts the platform media-element budget and
// crashes the tab, and it is dead weight anyway because phones render the
// separate MobileAthleteFeature / MobileAthleteDrawer stage instead. Gating the
// track on this flag keeps those elements from ever existing on mobile.
function useIsDesktop() {
    const [isDesktop, setIsDesktop] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches,
    );
    useEffect(() => {
        const mql = window.matchMedia(DESKTOP_QUERY);
        const onChange = () => setIsDesktop(mql.matches);
        onChange();
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);
    return isDesktop;
}

export function AthleteCarousel({ athletes }: AthleteCarouselProps) {
    const n = athletes.length;
    // Start in the middle copy so backward steps have left-side headroom.
    const [trackIdx, setTrackIdx] = useState(n);
    const [animating, setAnimating] = useState(false);
    const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
    const [mobileListOpen, setMobileListOpen] = useState(false);
    const [sectionRef, loaded] = useInView<HTMLElement>(REVEAL_OPTIONS);
    const viewportRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const mobileStageRef = useRef<HTMLDivElement>(null);
    const [viewportW, setViewportW] = useState(0);

    const isDesktop = useIsDesktop();

    // Card <video> elements by domIdx, so finish() can hand the expanded
    // video's playback position to the twin that replaces it on the snap.
    const videoEls = useRef<Map<number, HTMLVideoElement>>(new Map());
    const registerVideo = useCallback((index: number, el: HTMLVideoElement | null) => {
        if (el) { videoEls.current.set(index, el); }
        else { videoEls.current.delete(index); }
    }, []);

    // Live trackIdx for finish(), whose effect closure would otherwise be stale.
    const trackIdxRef = useRef(trackIdx);
    trackIdxRef.current = trackIdx;

    // Measure the viewport so the expanded card can fill it dynamically instead
    // of relying on a hardcoded width (mirrors the careers gallery approach).
    useEffect(() => {
        const el = viewportRef.current;
        if (!el) { return; }
        const measure = () => setViewportW(el.clientWidth);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [isDesktop]);

    useEffect(() => {
        setTrackIdx(n);
        setAnimating(false);
    }, [n]);

    // Snap trackIdx back into the middle copy once the transform transition
    // ends. setAnimating(false) drops the --animating class in the same batch
    // as the trackIdx change, so the widths and transform jump without
    // animating — the snap is invisible. The fallback timer guards against
    // transitionend not firing (tab backgrounded, prefers-reduced-motion
    // shortening duration to 0, etc.).
    useEffect(() => {
        const el = trackRef.current;
        if (!el || !animating) { return; }
        const finish = () => {
            const t = trackIdxRef.current;
            const target = n > 0 ? n + (((t % n) + n) % n) : 0;
            // Carry the expanded video's playback position onto the middle-copy
            // twin before React flips isExpanded, so the invisible snap doesn't
            // restart the clip from frame 0. Done imperatively here (not in the
            // setTrackIdx updater) so StrictMode's double-invoked updaters can't
            // double-fire this side effect.
            const from = videoEls.current.get(t);
            const to = videoEls.current.get(target);
            if (from && to && from !== to) {
                to.currentTime = from.currentTime;
            }
            setAnimating(false);
            setTrackIdx(target);
        };
        const onEnd = (e: TransitionEvent) => {
            if (e.target !== el || e.propertyName !== 'transform') { return; }
            finish();
        };
        const timer = window.setTimeout(finish, 700);
        el.addEventListener('transitionend', onEnd);
        return () => {
            window.clearTimeout(timer);
            el.removeEventListener('transitionend', onEnd);
        };
    }, [animating, n]);

    useEffect(() => {
        if (!mobileListOpen && !mobileProductsOpen) { return; }
        mobileStageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [mobileListOpen, mobileProductsOpen]);

    const expandedAthlete = n > 0
        ? athletes[((trackIdx % n) + n) % n]
        : athletes[0];

    const advance = (delta: number) => {
        if (animating || n <= 1) { return; }
        // Two-step rAF commit so the --animating class paints (transitions
        // turn on) before the trackIdx change. Otherwise the browser sees
        // both in one tick and skips interpolation, jumping to the new
        // layout instantly.
        setAnimating(true);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setTrackIdx((t) => t + delta);
            });
        });
    };

    const handleSelect = (domIdx: number) => {
        const offset = domIdx - trackIdx;
        // Only forward jumps inside the visible run: offset 0 is the open
        // card, offset >= n is an off-viewport copy.
        if (offset <= 0 || offset >= n) { return; }
        advance(offset);
    };

    const stepBy = (delta: number) => advance(delta);

    const handleMobileSelect = (id: string) => {
        const idx = athletes.findIndex((a) => a.id === id);
        if (idx === -1) { return; }
        // Mobile UI swaps athletes without the desktop slide; jump directly.
        setAnimating(false);
        setTrackIdx(n + idx);
        setMobileListOpen(false);
        setMobileProductsOpen(false);
    };

    // All cards before trackIdx are collapsed; the card at trackIdx is
    // expanded. translateX is the sum of widths strictly before trackIdx, so
    // it's trackIdx * (135 + 8) regardless of whether the card at trackIdx
    // happens to be wide or narrow at this instant.
    const translateX = trackIdx * (COLLAPSED_W + GAP);
    const total = n * COPIES;

    // The middle-copy twin the expanded card will snap to. Its <video> is the
    // only other element that needs to be mounted and warm, so the currentTime
    // handoff in finish() lands on a buffered element.
    const expandedAthleteIdx = n > 0 ? ((trackIdx % n) + n) % n : 0;
    const snapTargetIdx = n > 0 ? n + expandedAthleteIdx : 0;

    // Expanded card fills the viewport minus the other collapsed peeks and the
    // gaps between them, so it always fits without a hardcoded width. 0 before
    // the first measurement; the CSS width then acts as the fallback. Floor at
    // EXPANDED_MIN_W so a long roster doesn't squash it (extra peeks overflow
    // and clip), but never wider than the viewport itself.
    const availableW = viewportW - VIEWPORT_PADDING_X;
    const expandedW = viewportW > 0
        ? Math.min(availableW, Math.max(availableW - (n - 1) * (COLLAPSED_W + GAP), EXPANDED_MIN_W))
        : 0;

    return (
        <section ref={sectionRef} className={`athlete-carousel${loaded ? ' athlete-carousel--loaded' : ''}`}>
            <div className="athlete-carousel__header">
                <h2 className="athlete-carousel__heading">Explore our roster</h2>
                <div className="athlete-carousel__actions">
                    <Button label="explore all athletes" href="/roster" />
                    <div className="athlete-carousel__nav">
                        <CarouselArrow
                            direction="prev"
                            ariaLabel="Previous athlete"
                            onClick={() => stepBy(-1)}
                            disabled={animating || n <= 1}
                        />
                        <CarouselArrow
                            direction="next"
                            ariaLabel="Next athlete"
                            onClick={() => stepBy(1)}
                            disabled={animating || n <= 1}
                        />
                    </div>
                </div>
            </div>

            {isDesktop && (
                <div ref={viewportRef} className="athlete-carousel__viewport">
                    <div
                        ref={trackRef}
                        className={`athlete-carousel__track${animating ? ' athlete-carousel__track--animating' : ''}`}
                        style={{ transform: `translateX(${-translateX}px)` }}
                    >
                        {Array.from({ length: total }, (_, domIdx) => {
                            const athlete = athletes[domIdx % n];
                            const offset = domIdx - trackIdx;
                            const isExpanded = offset === 0;
                            const hidden = offset < 0 || offset >= n;
                            const inMiddleCopy = domIdx >= n && domIdx < 2 * n;
                            const staggerIdx = inMiddleCopy ? domIdx - n : 0;
                            // Only the expanded card and the twin it will snap to
                            // get a live <video>; every other copy stays as the
                            // <img> preview. Caps the desktop track at two media
                            // elements regardless of n, instead of n * COPIES.
                            const needsVideo = isExpanded || domIdx === snapTargetIdx;
                            const cardStyle = {
                                '--athlete-card-stagger': staggerIdx,
                                ...(isExpanded && expandedW > 0 ? { width: `${expandedW}px` } : {}),
                            } as React.CSSProperties;
                            return (
                                <AthleteCard
                                    key={domIdx}
                                    athlete={athlete}
                                    isExpanded={isExpanded}
                                    onClick={() => handleSelect(domIdx)}
                                    hidden={hidden}
                                    style={cardStyle}
                                    videoIndex={domIdx}
                                    mountVideo={needsVideo}
                                    eagerVideo={needsVideo}
                                    registerVideo={registerVideo}
                                />
                            );
                        })}
                    </div>
                </div>
            )}

            <div ref={mobileStageRef} className="athlete-carousel__mobile-stage">
                <MobileAthleteFeature
                    athlete={expandedAthlete}
                    productsOpen={mobileProductsOpen}
                    onToggleProducts={() => {
                        setMobileProductsOpen((isOpen) => !isOpen);
                        setMobileListOpen(false);
                    }}
                />
                <MobileAthleteDrawer
                    athletes={athletes}
                    selectedAthlete={expandedAthlete}
                    isOpen={mobileListOpen}
                    onToggle={() => {
                        setMobileListOpen((isOpen) => !isOpen);
                        setMobileProductsOpen(false);
                    }}
                    onClose={() => setMobileListOpen(false)}
                    onSelect={handleMobileSelect}
                />
            </div>
        </section>
    );
}
